import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { isPlanError } from '../../components/plan/planErrors';
import type { ApiError } from '../../lib/apiClient';
import { clearDraft, knowledgeBaseDraftKey } from '../../lib/draftStorage';
import { getErrorMessage } from '../../lib/errors';
import { useMediaQuery } from '../../lib/useMediaQuery';
import type {
  CalendarEventCreate,
  CalendarEventRead,
  CampaignRead,
  PlannedPostCreate,
  PlannedPostRead,
} from '../../types/api';
import { calendarEventsApi } from '../knowledge-base/knowledgeBaseApi';
import { useCollection } from '../knowledge-base/useCollection';
import { useBusiness } from '../onboarding/useBusiness';
import { isClaimed, isResettable } from './automation';
import { campaignsApi, plannedPostsApi, resetPlannedPost, runPlannedPostNow } from './calendarApi';
import {
  addDays,
  addMonths,
  currentMonth,
  datesBetween,
  isInMonth,
  localTimeOf,
  monthEnd,
  monthLabel,
  monthName,
  monthOf,
  monthStart,
  parseMonth,
  toInstant,
  todayIso,
} from './calendarDates';
import { plannedPostDraftKey } from './calendarDrafts';
import { CampaignManager } from './CampaignManager';
import { DayAgenda } from './DayAgenda';
import { GoalsModal } from './GoalsModal';
import { GoalsStrip } from './GoalsStrip';
import { KeyDateModal } from './KeyDateModal';
import { MonthGrid, type EventBand } from './MonthGrid';
import { NeedsReviewStrip } from './NeedsReviewStrip';
import { PlannedPostModal } from './PlannedPostModal';
import { ToolbarMore } from './ToolbarMore';
import { UpcomingList, type DayEntries } from './UpcomingList';
import { useContentGoals } from './useContentGoals';

const MONTH_PARAM = 'month';
const VIEW_PARAM = 'view';
const DAY_PARAM = 'day';

/**
 * The phone's calendar (ruling 11 of MOBILE_RESPONSIVE_PLAN.md): a dot grid
 * that picks a day, the day's agenda under it, and the toolbar's two quieter
 * actions behind More. Mirrors the `max-width: 640px` blocks in the calendar's
 * CSS.
 */
const CALENDAR_PHONE_QUERY = '(max-width: 640px)';

type CalendarView = 'month' | 'list';

/** Which dialog is up. Everything on this screen edits exactly one thing. */
type Dialog =
  | { kind: 'post'; post: PlannedPostRead | null; date: string }
  | { kind: 'key-date'; event: CalendarEventRead | null; date: string }
  | { kind: 'campaigns' }
  | { kind: 'goals' }
  | null;

/** Newest-first is right for a log; a day reads better oldest-first. */
function byCreatedAt(left: PlannedPostRead, right: PlannedPostRead): number {
  return left.created_at.localeCompare(right.created_at);
}

/** Planned posts filed under the day they are planned for. */
function groupPostsByDay(posts: readonly PlannedPostRead[]): Map<string, PlannedPostRead[]> {
  const byDay = new Map<string, PlannedPostRead[]>();
  for (const post of [...posts].sort(byCreatedAt)) {
    const day = byDay.get(post.scheduled_on);
    if (day) day.push(post);
    else byDay.set(post.scheduled_on, [post]);
  }
  return byDay;
}

/**
 * Key dates sliced into one band per day they cover, so a range reads as a run
 * across the grid. Clipped to a week either side of the month — the grid shows
 * a few days of each neighbour, and nothing looks up a day it does not render.
 */
function groupBandsByDay(
  events: readonly CalendarEventRead[],
  month: string,
): Map<string, EventBand[]> {
  const first = addDays(monthStart(month), -7);
  const last = addDays(monthEnd(month), 7);
  const byDay = new Map<string, EventBand[]>();

  for (const event of events) {
    const from = event.starts_on;
    const to = event.ends_on ?? event.starts_on;
    if (to < first || from > last) continue;

    for (const iso of datesBetween(from < first ? first : from, to > last ? last : to)) {
      const band: EventBand = { event, isStart: iso === from, isEnd: iso === to };
      const day = byDay.get(iso);
      if (day) day.push(band);
      else byDay.set(iso, [band]);
    }
  }
  return byDay;
}

/**
 * Everything from `fromIso` forward (through `toIso` when one is given), one
 * entry per day that has something on it. A multi-day key date is listed once,
 * on the first day of it inside the range.
 */
function buildUpcoming(
  posts: readonly PlannedPostRead[],
  events: readonly CalendarEventRead[],
  fromIso: string,
  toIso: string | null,
): DayEntries[] {
  const byDay = new Map<string, DayEntries>();

  function dayOf(iso: string): DayEntries {
    const existing = byDay.get(iso);
    if (existing) return existing;
    const created: DayEntries = { iso, events: [], posts: [] };
    byDay.set(iso, created);
    return created;
  }

  for (const event of events) {
    const to = event.ends_on ?? event.starts_on;
    if (to < fromIso) continue;
    if (toIso !== null && event.starts_on > toIso) continue;
    dayOf(event.starts_on < fromIso ? fromIso : event.starts_on).events.push(event);
  }

  for (const post of [...posts].sort(byCreatedAt)) {
    if (post.scheduled_on < fromIso) continue;
    if (toIso !== null && post.scheduled_on > toIso) continue;
    dayOf(post.scheduled_on).posts.push(post);
  }

  return [...byDay.values()].sort((left, right) => left.iso.localeCompare(right.iso));
}

/** One day's key dates (any that cover it) and planned posts, for the day agenda. */
function entriesOn(
  postsByDay: ReadonlyMap<string, PlannedPostRead[]>,
  events: readonly CalendarEventRead[],
  iso: string,
): DayEntries {
  return {
    iso,
    events: events.filter(
      (event) => event.starts_on <= iso && (event.ends_on ?? event.starts_on) >= iso,
    ),
    posts: postsByDay.get(iso) ?? [],
  };
}

function indexCampaigns(campaigns: readonly CampaignRead[]): Map<string, CampaignRead> {
  return new Map(campaigns.map((campaign) => [campaign.id, campaign]));
}

/**
 * The content calendar: what is planned, when, and whether the month adds up to
 * what the user said it was for.
 *
 * A plain planned post is an intention: it spends no quota, mints no request,
 * and only becomes a run when the user takes it to the generation form
 * themselves. A plan with `automation` set is the exception — the worker runs
 * it at its slot — and this screen is where its state is read and, when it
 * parks or fails, where it is handed back (`resetPlannedPost`).
 *
 * The visible month and view live in the URL, so a day linked from anywhere else
 * reopens on the same month.
 */
export function CalendarPage() {
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();
  const [searchParams, setSearchParams] = useSearchParams();

  const monthParam = searchParams.get(MONTH_PARAM);
  // An absent or malformed ?month= falls back rather than erroring: the URL is
  // user-editable and a calendar always has a month to show.
  const month =
    monthParam !== null && parseMonth(monthParam) !== null ? monthParam : currentMonth();
  const view: CalendarView = searchParams.get(VIEW_PARAM) === 'list' ? 'list' : 'month';
  const anchor = searchParams.get(DAY_PARAM);
  const isPhone = useMediaQuery(CALENDAR_PHONE_QUERY);

  const posts = useCollection(plannedPostsApi, businessId, 'planned_posts');
  const campaigns = useCollection(campaignsApi, businessId, 'campaigns');
  const events = useCollection(calendarEventsApi, businessId, 'calendar_events');
  const goals = useContentGoals(businessId, month);

  const [dialog, setDialog] = useState<Dialog>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  // A planned-post save the plan refused (Autopilot, a locked brand): the Upsell.
  const [postPlanError, setPostPlanError] = useState<ApiError | null>(null);

  const today = todayIso();
  /** A new post defaults to today when today is on screen, else to the first. */
  const defaultDate = isInMonth(today, month) ? today : monthStart(month);

  function updateParams(next: Partial<Record<string, string | null>>) {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value === null) params.delete(key);
      else if (value !== undefined) params.set(key, value);
    }
    // Replace, so browser back leaves the calendar instead of walking months.
    setSearchParams(params, { replace: true });
  }

  function openMonth(next: string) {
    updateParams({ [MONTH_PARAM]: next, [DAY_PARAM]: null });
  }

  function openView(next: CalendarView, day: string | null = null) {
    updateParams({ [VIEW_PARAM]: next, [DAY_PARAM]: day });
  }

  /** The dot grid's pick. A day borrowed from a neighbour opens its own month. */
  function selectDay(iso: string) {
    updateParams(
      isInMonth(iso, month)
        ? { [DAY_PARAM]: iso }
        : { [MONTH_PARAM]: monthOf(iso), [DAY_PARAM]: iso },
    );
  }

  function openCampaigns() {
    setWriteError(null);
    setDialog({ kind: 'campaigns' });
  }

  function openPost(post: PlannedPostRead | null, date: string) {
    setWriteError(null);
    setPostPlanError(null);
    setDialog({ kind: 'post', post, date });
  }

  function openKeyDate(event: CalendarEventRead | null, date: string) {
    setWriteError(null);
    setDialog({ kind: 'key-date', event, date });
  }

  async function savePost(body: PlannedPostCreate) {
    if (dialog?.kind !== 'post') return;
    const existing = dialog.post;
    setIsSaving(true);
    setWriteError(null);
    setPostPlanError(null);
    try {
      if (existing && isClaimed(existing)) {
        // The worker holds this plan: its slot is frozen (409 for any change
        // to it), so only the words and the campaign are sent.
        await posts.update(existing.id, {
          title: body.title,
          brief: body.brief,
          campaign_id: body.campaign_id,
        });
      } else if (existing) {
        await posts.update(existing.id, body);
      } else {
        await posts.create(body);
      }
      // The record is on file; the local copy has nothing left to protect.
      if (businessId) clearDraft(plannedPostDraftKey(businessId, existing));
      // Saving onto another month should land the user where the post went.
      if (!isInMonth(body.scheduled_on, month)) openMonth(monthOf(body.scheduled_on));
      setDialog(null);
    } catch (caught) {
      if (isPlanError(caught)) setPostPlanError(caught);
      else setWriteError(getErrorMessage(caught, 'That post did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  async function saveKeyDate(body: CalendarEventCreate) {
    if (dialog?.kind !== 'key-date') return;
    const existing = dialog.event;
    setIsSaving(true);
    setWriteError(null);
    try {
      if (existing) await events.update(existing.id, body);
      else await events.create(body);
      // Same key the knowledge-base form writes under — one record, one draft.
      if (businessId) {
        clearDraft(knowledgeBaseDraftKey('calendar', businessId, existing?.id ?? null));
      }
      setDialog(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That event did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the chip moves now, the PUT follows. */
  function movePost(postId: string, iso: string) {
    const post = posts.items.find((item) => item.id === postId);
    if (!post || post.scheduled_on === iso) return;
    // A plan the worker holds cannot move (the chip refuses the drag too); an
    // automated plan that can carries its instant with it to the new day.
    if (isClaimed(post)) return;
    if (post.automation !== 'none' && post.publish_at) {
      // Same local time of day on the new day, recomposed in the viewer's
      // offset — the same instant shifted by whole days would drift across a
      // clock change.
      const time = localTimeOf(post.publish_at);
      const publishAt = time === null ? null : toInstant(iso, time);
      setWriteError(null);
      void posts
        .update(postId, { scheduled_on: iso, publish_at: publishAt ?? post.publish_at })
        .catch((caught: unknown) => {
          setWriteError(getErrorMessage(caught, 'That post could not be moved.'));
        });
      return;
    }
    setWriteError(null);
    void posts.update(postId, { scheduled_on: iso }).catch((caught: unknown) => {
      setWriteError(getErrorMessage(caught, 'That post could not be moved.'));
    });
  }

  /** A parked or failed plan back to the scheduler. The row updates in place. */
  async function resetPost(post: PlannedPostRead) {
    if (!businessId || !isResettable(post)) return;
    setWriteError(null);
    try {
      posts.replace(await resetPlannedPost(businessId, post.id));
      setDialog(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That plan could not be reset.'));
    }
  }

  /** Start an idle automated plan now. The row updates in place. */
  async function runPostNow(post: PlannedPostRead) {
    if (!businessId) return;
    setWriteError(null);
    try {
      posts.replace(await runPlannedPostNow(businessId, post.id));
      setDialog(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That plan could not be started.'));
    }
  }

  function deletePost(post: PlannedPostRead) {
    setDialog(null);
    posts.removeWithUndo({
      id: post.id,
      label: post.title,
      failureMessage: 'That planned post could not be deleted.',
    });
  }

  // Everything below is derived during render — the React Compiler owns
  // memoisation, so nothing here is wrapped in useMemo.
  const monthPosts = posts.items.filter((post) => isInMonth(post.scheduled_on, month));
  const postsByDay = groupPostsByDay(posts.items);
  const bandsByDay = groupBandsByDay(events.items, month);
  const campaignsById = indexCampaigns(campaigns.items);
  // The list follows ‹ ›: another month lists that month alone, from its first
  // to its last; the current month lists today forward, as it always has.
  const isOtherMonth = monthParam !== null && month !== currentMonth();
  const listStart = isOtherMonth ? monthStart(month) : today;
  const listEnd = isOtherMonth ? monthEnd(month) : null;
  // An anchor outside the range still shows its own day, so a "+N more" on a
  // borrowed day always lands.
  const upcomingFrom = anchor !== null && anchor < listStart ? anchor : listStart;
  const upcomingTo = listEnd !== null && anchor !== null && anchor > listEnd ? anchor : listEnd;
  const upcoming = buildUpcoming(posts.items, events.items, upcomingFrom, upcomingTo);
  const needsReview = posts.items.filter((post) => post.automation_status === 'needs_review');
  // The phone's agenda: the picked day when it is on this month, else today
  // when today is, else the first.
  const agendaDay = anchor !== null && isInMonth(anchor, month) ? anchor : defaultDate;

  const loadError = posts.error ?? events.error ?? campaigns.error ?? posts.deleteError;

  return (
    <main className="page page--wide">
      <PageHeader
        section="Calendar"
        title="Calendar"
        lede="What is going out, when, and what the month is for."
      />

      {isBusinessLoading ? (
        <p className="kb-section__status" role="status">
          Loading your business
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {businessError ??
              'No business is linked to this account yet. Finish setup and the calendar opens.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <>
          <div className="cal-toolbar">
            <div className="cal-toolbar__month">
              <button
                className="button button--ghost"
                type="button"
                aria-label="Previous month"
                onClick={() => openMonth(addMonths(month, -1))}
              >
                ‹
              </button>
              <h2 className="cal-toolbar__label">{monthLabel(month)}</h2>
              <button
                className="button button--ghost"
                type="button"
                aria-label="Next month"
                onClick={() => openMonth(addMonths(month, 1))}
              >
                ›
              </button>
              <button
                className="button button--ghost"
                type="button"
                disabled={month === currentMonth()}
                onClick={() => openMonth(currentMonth())}
              >
                Today
              </button>
            </div>

            <div className="cal-toolbar__views" role="group" aria-label="Calendar view">
              <button
                className={view === 'month' ? 'cal-view cal-view--active' : 'cal-view'}
                type="button"
                aria-pressed={view === 'month'}
                onClick={() => openView('month')}
              >
                Month
              </button>
              <button
                className={view === 'list' ? 'cal-view cal-view--active' : 'cal-view'}
                type="button"
                aria-pressed={view === 'list'}
                onClick={() => openView('list')}
              >
                List
              </button>
            </div>

            <div className="cal-toolbar__actions">
              {isPhone ? (
                <ToolbarMore
                  onAddKeyDate={() => openKeyDate(null, defaultDate)}
                  onManageCampaigns={openCampaigns}
                />
              ) : (
                <>
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => openKeyDate(null, defaultDate)}
                  >
                    Add event
                  </button>
                  <button className="button button--ghost" type="button" onClick={openCampaigns}>
                    Manage campaigns
                  </button>
                </>
              )}
              {/* The one accent-filled action on this screen. */}
              <button
                className="button button--primary"
                type="button"
                onClick={() => openPost(null, defaultDate)}
              >
                Plan post
              </button>
            </div>
          </div>

          <GoalsStrip
            month={month}
            goals={goals.goals}
            isLoading={goals.isLoading}
            error={goals.error}
            monthPosts={monthPosts}
            onEdit={() => {
              setWriteError(null);
              setDialog({ kind: 'goals' });
            }}
          />

          {loadError ? (
            <p className="form__error" role="alert">
              {loadError}
            </p>
          ) : null}

          <NeedsReviewStrip
            posts={needsReview}
            onOpen={(post) => openPost(post, post.scheduled_on)}
            onReset={(post) => void resetPost(post)}
          />

          {writeError && dialog === null ? (
            <p className="form__error" role="alert">
              {writeError}
            </p>
          ) : null}

          {posts.isLoading ? (
            <p className="kb-section__status" role="status">
              Loading the calendar
            </p>
          ) : view === 'month' ? (
            <>
              <MonthGrid
                month={month}
                today={today}
                postsByDay={postsByDay}
                bandsByDay={bandsByDay}
                campaignsById={campaignsById}
                onOpenPost={(post) => openPost(post, post.scheduled_on)}
                onOpenEvent={(event) => openKeyDate(event, event.starts_on)}
                onQuickAdd={(iso) => openPost(null, iso)}
                onShowDay={(iso) => openView('list', iso)}
                onMovePost={movePost}
                variant={isPhone ? 'dots' : 'full'}
                selectedDay={agendaDay}
                onSelectDay={selectDay}
              />

              {isPhone ? (
                <DayAgenda
                  day={entriesOn(postsByDay, events.items, agendaDay)}
                  campaignsById={campaignsById}
                  onOpenPost={(post) => openPost(post, post.scheduled_on)}
                  onOpenEvent={(event) => openKeyDate(event, event.starts_on)}
                  onResetPost={(post) => void resetPost(post)}
                  onPlanPost={(iso) => openPost(null, iso)}
                />
              ) : monthPosts.length === 0 ? (
                <div className="kb-section__empty">
                  <p className="kb-section__empty-text">
                    Nothing planned for {monthName(month)}. Plan your first post.
                  </p>
                  <button
                    className="button"
                    type="button"
                    onClick={() => openPost(null, defaultDate)}
                  >
                    Plan a post
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <UpcomingList
              days={upcoming}
              campaignsById={campaignsById}
              anchor={anchor}
              emptyText={
                isOtherMonth
                  ? `Nothing on the calendar for ${monthName(month)}.`
                  : 'Nothing ahead on the calendar.'
              }
              onOpenPost={(post) => openPost(post, post.scheduled_on)}
              onOpenEvent={(event) => openKeyDate(event, event.starts_on)}
              onPlanPost={() => openPost(null, defaultDate)}
              onResetPost={(post) => void resetPost(post)}
            />
          )}

          {dialog?.kind === 'post' ? (
            <PlannedPostModal
              businessId={businessId}
              initial={dialog.post}
              defaultDate={dialog.date}
              campaigns={campaigns.items}
              isSaving={isSaving}
              error={writeError}
              planError={postPlanError}
              onSubmit={(body) => void savePost(body)}
              onDelete={deletePost}
              onReset={(post) => void resetPost(post)}
              onRunNow={(post) => void runPostNow(post)}
              onDismiss={() => setDialog(null)}
            />
          ) : null}

          {dialog?.kind === 'key-date' ? (
            <KeyDateModal
              businessId={businessId}
              initial={dialog.event}
              defaultDate={dialog.date}
              isSaving={isSaving}
              error={writeError}
              onSubmit={(body) => void saveKeyDate(body)}
              onDismiss={() => setDialog(null)}
            />
          ) : null}

          {dialog?.kind === 'campaigns' ? (
            <CampaignManager
              campaigns={campaigns.items}
              onCreate={campaigns.create}
              onUpdate={campaigns.update}
              onRemove={campaigns.remove}
              onDismiss={() => setDialog(null)}
            />
          ) : null}

          {dialog?.kind === 'goals' ? (
            <GoalsModal
              month={month}
              goals={goals.goals}
              onSave={goals.save}
              onDismiss={() => setDialog(null)}
            />
          ) : null}
        </>
      )}
    </main>
  );
}
